import { WORLD_PAGE } from '../server/meta.ts';
import { inBox } from '@tol/core/onscreen';
import type { Box } from '@tol/core/onscreen';
import type { TrainPrediction } from '@tol/core/types';
import { selectTitlePlace } from './title-selection.ts';
import type { TitleArea, TitleCamera } from './title-selection.ts';
import type { CountryView } from './country-view.ts';
import { rosterScope } from './roster.ts';
import type { RosterNetwork, RosterScope } from './roster.ts';

export interface ContextPlace extends TitleArea {
  readonly name: string;
  readonly slug: string;
  readonly timeZone: string;
  readonly locale: string;
}

export interface PlaceContext {
  readonly place: ContextPlace | undefined;
  readonly title: string;
  readonly path: string;
  readonly scope: RosterScope;
}

export function createGpsContext(): {
  readonly update: (
    trains: Readonly<
      Iterable<{ readonly dead: boolean; readonly pred: Readonly<Pick<TrainPrediction, 'lineId' | 'gpsPoint'>> }>
    >,
    bounds: Box,
    now: number,
  ) => boolean;
  readonly lines: () => ReadonlySet<string>;
} {
  let lines = new Set<string>();
  return {
    update(trains, bounds, now) {
      const next = new Set<string>();
      for (const train of trains) {
        const point = train.pred.gpsPoint;
        if (!train.dead && point && now - point.at < 180_000 && inBox(bounds, point)) next.add(train.pred.lineId);
      }
      if (next.size === lines.size && [...next].every((line) => lines.has(line))) return false;
      lines = next;
      return true;
    },
    lines: () => lines,
  };
}

export function selectPlaceContext(
  camera: TitleCamera,
  places: readonly ContextPlace[],
  opening: CountryView | null,
  networks: readonly RosterNetwork[],
  gpsLines: ReadonlySet<string>,
): PlaceContext {
  const id = selectTitlePlace(camera, places, opening);
  const place = places.find((p) => p.id === id);
  return {
    place,
    title: place ? `${place.name}, live` : WORLD_PAGE.title,
    path: place?.slug ?? '.',
    scope: rosterScope(camera, networks, [], gpsLines, id),
  };
}
